// @ts-nocheck
import { useMemo, useState } from 'react';
import { Bell, BellOff, Loader2, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useToast } from '@/hooks/use-toast';

const DISMISS_KEY = 'pwa-push-banner-dismissed-until';

/**
 * Banner fixo que incentiva o usuário a ativar notificações push.
 * Aparece apenas quando:
 *   - user está logado
 *   - browser suporta push
 *   - permissão NÃO está concedida
 *   - o user ainda não fechou o banner nas últimas 24h
 *
 * Sem isso, usuários novos nunca recebem push (nem a notificação imediata
 * na criação nem os lembretes do cron), porque não há push_subscription
 * salva no Supabase pra eles.
 */
export const MobilePushPermissionBanner = () => {
  const { user } = useAuth();
  const { status, subscribe, error } = usePushNotifications();
  const { toast } = useToast();

  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      return raw ? Number(raw) : 0;
    } catch {
      return 0;
    }
  });

  const isHidden = useMemo(() => {
    if (!user) return true;
    if (status === 'loading' || status === 'subscribed' || status === 'unsupported') return true;
    if (dismissedAt && Date.now() < dismissedAt) return true;
    return false;
  }, [user, status, dismissedAt]);

  if (isHidden) return null;

  const dismiss = () => {
    const until = Date.now() + 24 * 60 * 60 * 1000; // 24h
    try {
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {}
    setDismissedAt(until);
  };

  const handleActivate = async () => {
    const ok = await subscribe();
    if (ok) {
      toast({
        title: 'Notificações ativadas',
        description: 'Você vai receber alertas de agendamentos, tarefas e prazos.',
      });
    } else {
      toast({
        title: 'Não foi possível ativar',
        description: error || 'Permita as notificações nas configurações do navegador.',
        variant: 'destructive',
      });
    }
  };

  const isBlocked = status === 'denied';
  const isBusy = status === 'subscribing';

  return (
    <div
      className={`px-4 py-2.5 border-b flex items-center gap-2.5 text-xs text-left ${
        isBlocked ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-[#faf7f2] text-slate-700 border-[#c4ab83]/30'
      }`}
    >
      <div className={isBlocked ? '' : 'text-[#a88a5c]'}>
        {isBlocked ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold leading-tight">
          {isBlocked ? 'Notificações bloqueadas' : 'Ative as notificações'}
        </p>
        <p className="leading-snug opacity-90 mt-0.5">
          {isBlocked
            ? 'Você bloqueou notificações deste app. Abra as configurações do navegador e libere para receber lembretes.'
            : 'Sem isso, você não vai receber lembretes de agendamentos, tarefas e prazos.'}
        </p>
      </div>
      {!isBlocked && (
        <button
          type="button"
          disabled={isBusy}
          onClick={handleActivate}
          className="shrink-0 h-8 px-3.5 rounded-full bg-slate-900 text-[#e8dcc4] font-medium flex items-center gap-1.5 active:scale-95 transition disabled:opacity-60"
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
          Ativar
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Fechar"
        className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
